import React, { useState, useEffect, useRef } from "react";
import { FaRegCalendarAlt, FaChevronDown } from "react-icons/fa";
import { Globe } from "lucide-react";
import { DayPicker } from "react-day-picker";
import {
  format,
  setMonth,
  setYear,
  getYear,
} from "date-fns";
import "react-day-picker/dist/style.css";

const TIMEZONES = [
  "IST", "EST", "PST", "CST", "GMT", "UTC",
  "BST", "CET", "EET", "JST", "AEST", "NZST"
];

// Convert "YYYY-MM-DD" to a local Date at midnight without UTC shift.
// If already a Date, just return it.
const toLocalDate = (s) => {
  if (s instanceof Date) return s;
  if (typeof s === "string") {
    const [y, m, d] = s.split("-").map(Number);
    return new Date(y, m - 1, d);
  }
  // Fallback: now
  return new Date();
};

const DateTimezonePicker = ({ date, timezone, onDateChange, onTimezoneChange }) => {
  const [showPanel, setShowPanel] = useState(false);
  const [showTzDropdown, setShowTzDropdown] = useState(false);
  const [calendarMonth, setCalendarMonth] = useState(toLocalDate(date));
  const wrapperRef = useRef(null);

  // always treat selected date as local, not UTC
  const selectedDate = toLocalDate(date);

 

  const handleDayClick = (day) => {
    // Save as local YYYY-MM-DD to avoid timezone shifts
    onDateChange(format(day, "yyyy-MM-dd"));
    setShowPanel(false);
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setShowPanel(false);
        setShowTzDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    setCalendarMonth(toLocalDate(date));
  }, [date]);

  const handleMonthSelect = (monthIndex) => {
    setCalendarMonth((prev) => setMonth(prev, monthIndex));
  };

  const handleYearChange = (increment) => {
    setCalendarMonth((prev) => setYear(prev, getYear(prev) + increment));
  };

 return (
  <div
    ref={wrapperRef}
    className="relative flex items-center 
               border border-gray-300 dark:border-acs-dark-border 
               rounded-full 
               bg-[#FAFAFB] dark:bg-acs-dark-surface 
               text-fluid-xs w-fit transition-none hover:bg-gray-100 dark:hover:bg-acs-dark-hover"
  >
    {/* 📅 Week Picker */}
    <div className="relative flex items-center pl-fluid-2 pr-fluid-1">
      <FaRegCalendarAlt className="text-custom-bg-blue dark:text-acs-accent w-fluid-icon-sm h-fluid-icon-sm mr-fluid-1" />
      <button
        type="button"
        onClick={() => setShowPanel(!showPanel)}
        className="bg-transparent text-fluid-base py-fluid-button-y pr-fluid-button-x 
                   appearance-none cursor-pointer focus:outline-none 
                   text-gray-800 dark:text-acs-dark-text transition-none"
      >
        {format(selectedDate, "MMM dd, yyyy")}
      </button>

      {/* Calendar Panel */}
      {showPanel && (
        <div className="absolute top-[120%] p-fluid-1 z-50 
                        bg-white dark:bg-acs-dark-surface 
                        border border-gray-200 dark:border-acs-dark-border 
                        rounded-xl shadow-xl flex text-fluid-sm overflow-hidden transition-none">
          {/* Left: Day Picker */}
          <div className="p-fluid-2 border-r border-gray-300 dark:border-acs-dark-border">
            <DayPicker
              mode="single"
              weekStartsOn={1}
              selected={selectedDate}
              onDayClick={handleDayClick}
              month={calendarMonth}
              onMonthChange={setCalendarMonth}
              showOutsideDays
              // use a custom modifier name to highlight the entire week
              className="text-gray-800 dark:text-acs-dark-text"
            />
          </div>

          {/* Right: Month + Year nav */}
          <div className="w-40 p-fluid-2">
            <div className="text-center font-medium text-gray-800 dark:text-acs-dark-text mb-fluid-1">
              {getYear(calendarMonth)}
            </div>
            <div className="flex justify-between mb-fluid-1">
              <button
                onClick={() => handleYearChange(-1)}
                className="text-fluid-xs text-gray-600 dark:text-acs-dark-subtext hover:text-black dark:hover:text-acs-dark-text transition-none"
              >
                ↑
              </button>
              <button
                onClick={() => handleYearChange(1)}
                className="text-fluid-xs text-gray-600 dark:text-acs-dark-subtext hover:text-black dark:hover:text-acs-dark-text transition-none"
              >
                ↓
              </button>
            </div>
            <div className="grid grid-cols-3 gap-fluid-0.5">
              {[
                "Jan", "Feb", "Mar", "Apr", "May", "Jun",
                "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
              ].map((m, idx) => (
                <button
                  key={m}
                  className={`rounded px-fluid-1.5 py-fluid-1 
                              hover:bg-gray-100 dark:hover:bg-acs-dark-hover transition-none ${
                                calendarMonth.getMonth() === idx
                                  ? "bg-custom-bg-blue text-white"
                                  : "text-gray-800 dark:text-acs-dark-text"
                              }`}
                  onClick={() => handleMonthSelect(idx)}
                >
                  {m}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>

    {/* 🌐 Timezone Dropdown */}
    <div className="relative">
      {/* Trigger (if you want to show the dropdown trigger) */}
      {/* <button
        type="button"
        onClick={() => setShowTzDropdown((prev) => !prev)}
        className="flex items-center gap-fluid-1 text-custom-bg-blue dark:text-acs-accent text-fluid-base focus:outline-none"
      >
        {timezone}
        <FaChevronDown
          className={`w-fluid-icon-xs h-fluid-icon-xs transition-transform ${
            showTzDropdown ? "rotate-180" : ""
          }`}
        />
      </button> */}

      {showTzDropdown && (
        <div className="absolute top-full mt-fluid-3 z-50 
                        bg-white dark:bg-acs-dark-surface 
                        border border-gray-200 dark:border-acs-dark-border 
                        rounded-xl shadow-xl flex text-fluid-sm overflow-hidden transition-none">
          <div className="flex flex-col p-fluid-1.5 w-28">
            {TIMEZONES.map((tz) => (
              <button
                key={tz}
                onClick={() => {
                  onTimezoneChange(tz);
                  setShowTzDropdown(false);
                }}
                className="flex items-center gap-fluid-1 px-fluid-2 py-fluid-1.5 
                           text-gray-600 dark:text-acs-dark-text 
                           text-fluid-xs sm:text-fluid-sm 
                           hover:bg-gray-50 dark:hover:bg-acs-dark-hover 
                           transition-colors rounded-md whitespace-nowrap"
              >
                <Globe className="w-fluid-icon-xs h-fluid-icon-xs text-[#1e2a74] dark:text-acs-accent" />
                {tz}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  </div>
);
};

export default DateTimezonePicker;
